import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Heart, Flame, Rocket, Beaker, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";

const STATUS_META = {
  idea:      { label: "Idea Stage",       tone: "border-brand-charcoal/30 text-brand-charcoal/70 bg-white",  rail: "#E6DDD3" },
  research:  { label: "Research Stage",   tone: "border-blue-200 bg-blue-50 text-blue-700",                  rail: "#7B9EFF" },
  testing:   { label: "Testing Stage",    tone: "border-amber-200 bg-amber-50 text-amber-700",               rail: "#F5A623" },
  voting:    { label: "Customer Voting",  tone: "border-brand-red/30 bg-brand-red/5 text-brand-red",         rail: "#FF4F5A" },
  launching: { label: "Launching Soon",   tone: "border-emerald-200 bg-emerald-50 text-emerald-700",         rail: "#34D399" },
  released:  { label: "Released",         tone: "border-brand-charcoal bg-brand-charcoal text-brand-cream",  rail: "#2B2B2B" },
};
const statusOf = (k) => STATUS_META[k] || STATUS_META.idea;

const REACTIONS = [
  { key: "love",     label: "Love It",   Icon: Heart,  color: "#FF4F5A" },
  { key: "must_try", label: "Must Try",  Icon: Flame,  color: "#FFC93C" },
  { key: "make_now", label: "Make Now",  Icon: Rocket, color: "#2B2B2B" },
];

const STAGE_ORDER = ["voting", "testing", "research", "launching", "idea", "released"];

export default function FlavourLab() {
  const [items, setItems] = useState([]);
  const [voted, setVoted] = useState({});
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/lab-flavours").then((r) => setItems(r.data || []));
    setVoted(JSON.parse(localStorage.getItem("pp_lab_votes") || "{}"));
  }, []);

  const vote = async (id, reaction) => {
    if (voted[id]) return;
    try {
      const { data } = await api.post(`/lab-flavours/${id}/vote`, { reaction });
      setItems((arr) => arr.map((s) => s.id === id ? { ...s, votes: data.votes } : s));
      const nv = { ...voted, [id]: reaction };
      setVoted(nv);
      localStorage.setItem("pp_lab_votes", JSON.stringify(nv));
      toast.success("Vote counted!");
    } catch { toast.error("Voting unavailable"); }
  };

  const filtered = useMemo(() => filter === "all" ? items : items.filter((x) => x.status === filter), [items, filter]);
  const stages = useMemo(() => {
    const set = new Set(items.map((i) => i.status));
    return ["all", ...STAGE_ORDER.filter((s) => set.has(s))];
  }, [items]);

  const featured = items.find((x) => x.status === "voting") || items[0];

  return (
    <div data-testid="lab-page" className="section-pad">
      <div className="container-pp">
        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="editorial-divider mb-5" />
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red flex items-center gap-2"><Beaker size={12} /> Pop Panda · Flavour Lab</div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="editorial-h1 text-5xl md:text-7xl lg:text-8xl mt-4 leading-[0.95]">
              The flavours we're <em className="font-display italic">currently</em> exploring.
            </motion.h1>
          </div>
          <div className="lg:col-span-4 text-brand-charcoal/70 leading-relaxed">
            <p>Some are barely an idea. Some are in a freezer right now. A few are open for community voting.</p>
            <p className="mt-3 text-brand-charcoal">Welcome to the lab — pull on the lab coat.</p>
          </div>
        </div>

        {/* Featured voting card */}
        {featured && featured.voting_enabled && (
          <article className="mt-16 grid lg:grid-cols-12 overflow-hidden rounded-[2rem] card-elevated" data-testid={`lab-featured-${featured.id}`}>
            <div className="lg:col-span-6 relative aspect-[5/4] lg:aspect-auto overflow-hidden bg-brand-muted">
              {featured.image_url
                ? <img src={featured.image_url} alt={featured.name} className="absolute inset-0 h-full w-full object-cover" />
                : <div className="absolute inset-0 grid place-items-center text-7xl">🧪</div>}
              <span className={`absolute top-5 left-5 text-[10px] uppercase tracking-[0.22em] border rounded-full px-3 py-1 ${statusOf(featured.status).tone}`}>{statusOf(featured.status).label}</span>
            </div>
            <div className="lg:col-span-6 p-8 lg:p-12 flex flex-col">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-brand-red animate-pulse" /> Featured · Community is voting now
              </div>
              <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.05]">{featured.name}</h2>
              {featured.description && <p className="mt-4 text-brand-charcoal/70 leading-relaxed">{featured.description}</p>}
              <div className="mt-auto pt-8">
                <div className="text-[10px] uppercase tracking-[0.2em] text-brand-charcoal/55 mb-3">
                  Cast your vote · {Object.values(featured.votes || {}).reduce((a, b) => a + b, 0)} votes so far
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {REACTIONS.map((r) => {
                    const did = voted[featured.id] === r.key;
                    const count = featured.votes?.[r.key] || 0;
                    return (
                      <button key={r.key} data-testid={`lab-vote-${featured.id}-${r.key}`} onClick={() => vote(featured.id, r.key)} disabled={!!voted[featured.id]}
                        className={`flex flex-col items-center gap-1.5 py-4 rounded-2xl border text-xs transition-all ${did ? "border-brand-charcoal bg-brand-yellow/40 scale-[1.02]" : "border-brand-line hover:bg-brand-muted hover:-translate-y-0.5"}`}>
                        <r.Icon size={18} style={{ color: r.color }} />
                        <span className="font-medium">{r.label}</span>
                        <span className="font-display text-2xl text-brand-charcoal">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </article>
        )}

        {/* Stage filter */}
        {stages.length > 2 && (
          <div className="mt-14 flex flex-wrap gap-2" data-testid="lab-filter">
            {stages.map((s) => {
              const active = filter === s;
              const label = s === "all" ? "All stages" : statusOf(s).label;
              return (
                <button key={s} data-testid={`lab-filter-${s}`} onClick={() => setFilter(s)}
                  className={`btn-pill text-xs ${active ? "btn-dark" : "btn-ghost border border-brand-line"}`}>{label}</button>
              );
            })}
          </div>
        )}

        {/* Grid */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="lab-grid">
          {filtered.filter((x) => x.id !== featured?.id || !featured?.voting_enabled).map((lf, i) => {
            const total = Object.values(lf.votes || {}).reduce((a, b) => a + b, 0);
            const s = statusOf(lf.status);
            return (
              <motion.article key={lf.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.45, delay: (i % 6) * 0.04 }}
                className="card-elevated overflow-hidden flex flex-col"
                data-testid={`lab-${lf.id}`}>
                <div className="h-1.5 w-full" style={{ background: s.rail }} />
                <div className="aspect-[4/3] bg-brand-muted overflow-hidden">
                  {lf.image_url
                    ? <img src={lf.image_url} alt={lf.name} className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" />
                    : <div className="h-full w-full grid place-items-center text-brand-charcoal/30 text-5xl">🧪</div>}
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-2xl leading-tight">{lf.name}</h3>
                    <span className={`text-[10px] uppercase tracking-[0.18em] border rounded-full px-2 py-0.5 shrink-0 ${s.tone}`}>{s.label}</span>
                  </div>
                  {lf.description && <p className="mt-3 text-sm text-brand-charcoal/70 leading-relaxed">{lf.description}</p>}
                  {lf.voting_enabled ? (
                    <div className="mt-5 pt-5 border-t border-brand-line">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-brand-charcoal/55 mb-2">Community vote · {total} so far</div>
                      <div className="grid grid-cols-3 gap-2">
                        {REACTIONS.map((r) => {
                          const did = voted[lf.id] === r.key;
                          const count = lf.votes?.[r.key] || 0;
                          return (
                            <button key={r.key} data-testid={`lab-vote-${lf.id}-${r.key}`} onClick={() => vote(lf.id, r.key)} disabled={!!voted[lf.id]}
                              className={`flex flex-col items-center gap-1 py-2 rounded-2xl border text-xs transition-all ${did ? "border-brand-charcoal bg-brand-yellow/30" : "border-brand-line hover:bg-brand-muted"}`}>
                              <r.Icon size={14} style={{ color: r.color }} />
                              <span className="font-medium">{r.label}</span>
                              <span className="text-[10px] text-brand-charcoal/65">{count}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-5 pt-5 border-t border-brand-line text-[10px] uppercase tracking-[0.2em] text-brand-charcoal/45">Voting closed for this flavour</div>
                  )}
                </div>
              </motion.article>
            );
          })}
          {filtered.length === 0 && <div className="text-brand-charcoal/60 col-span-full">No flavours in this stage right now.</div>}
        </div>

        {/* CTA */}
        <div className="mt-24 card-elevated p-10 lg:p-14 grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Be an inventor</div>
            <h3 className="font-display text-4xl md:text-5xl mt-3 leading-none">Got a flavour in mind? Drop it in our lab.</h3>
            <p className="mt-4 text-brand-charcoal/70 max-w-xl">Submit your own recipe through the Flavour Creator Studio. Promising ones go into our test rotation — with your name on them.</p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/flavour-creator" className="btn-pill btn-dark" data-testid="lab-cta">Create Your Own Popsicle <ArrowUpRight size={16} className="arrow" /></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
